import React, { useEffect, useMemo, useRef, useState } from 'react';

// Molecular theme graph — svg + orbs + bonds + ambient particle field

const THEME_COLORS = {
  cyan: '#20B8F2',
  mint: '#7EE06D',
  violet: '#A78BFA',
  amber: '#F2B84C',
  coral: '#F2704C',
};

const PRIMARY_ORBS = [
  { id: 'cyan',   label: 'HARD TRUTHS',    color: THEME_COLORS.cyan,   x: 26, y: 32, r: 24 },
  { id: 'violet', label: 'TURNING POINTS', color: THEME_COLORS.violet, x: 70, y: 24, r: 18 },
  { id: 'mint',   label: 'ADVICE',         color: THEME_COLORS.mint,   x: 78, y: 68, r: 18 },
  { id: 'amber',  label: 'INSIGHTS',       color: THEME_COLORS.amber,  x: 30, y: 78, r: 16 },
  { id: 'coral',  label: 'MOMENTS',        color: THEME_COLORS.coral,  x: 52, y: 50, r: 15 },
];

const SUB_ORBS = [
  { id: 'sub1', color: THEME_COLORS.cyan,   x: 42, y: 18, r: 8 },
  { id: 'sub2', color: THEME_COLORS.violet, x: 90, y: 46, r: 9 },
  { id: 'sub3', color: THEME_COLORS.mint,   x: 12, y: 56, r: 8 },
];

const BONDS = [
  { from: 'cyan',   to: 'violet', pri: true },
  { from: 'violet', to: 'mint',   pri: true },
  { from: 'mint',   to: 'amber',  pri: true },
  { from: 'amber',  to: 'cyan',   pri: true },
  { from: 'cyan',   to: 'mint',   pri: true },
  { from: 'violet', to: 'amber',  pri: true },
  { from: 'coral',  to: 'cyan',   pri: true },
  { from: 'coral',  to: 'mint',   pri: true },
  { from: 'sub1',   to: 'cyan',   pri: false },
  { from: 'sub2',   to: 'violet', pri: false },
  { from: 'sub3',   to: 'amber',  pri: false },
];

const PARTICLE_COUNT = 42;
const PARTICLE_COLORS_WEIGHTED = [
  ...Array(13).fill(THEME_COLORS.cyan),
  ...Array(11).fill(THEME_COLORS.violet),
  ...Array(9).fill(THEME_COLORS.mint),
  ...Array(6).fill(THEME_COLORS.amber),
  ...Array(4).fill(THEME_COLORS.coral),
];

const PHASE_LABEL = {
  idle: null,
  uploading: 'UPLOADING',
  transcribing: 'TRANSCRIBING',
  detecting: 'DETECTING',
  slicing: 'SLICING',
  titling: 'TITLING',
  complete: 'MAPPED',
};

function findOrb(id) {
  return [...PRIMARY_ORBS, ...SUB_ORBS].find(o => o.id === id);
}

/** Infer live-map phase from processing logs + console mode. */
export function deriveGraphPhase(consoleMode, logs = []) {
  if (consoleMode === 'idle') return 'idle';
  if (consoleMode === 'complete') return 'complete';
  const texts = logs.map((l) => String(l?.text || '').toLowerCase());
  const last = texts[texts.length - 1] || '';
  const joined = texts.join(' | ');
  if (/summary key|title key|dual.?key|generat.*title|card titles|snippet insights|titles ready/.test(last)
    || /summary key|title key|dual.?key|generat.*title|card titles|titles ready/.test(joined)) {
    return 'titling';
  }
  if (/slic/.test(last) || (/slic/.test(joined) && !/title/.test(last))) return 'slicing';
  if (/detect|highlights detected|theme/.test(last) || /detecting highlights/.test(joined)) return 'detecting';
  if (/transcrib/.test(last) || /transcrib/.test(joined)) return 'transcribing';
  if (/upload|source ·|run started/.test(last) || /upload/.test(joined)) return 'uploading';
  return 'uploading';
}

export default function ThemeGraph({ active, activeThemes = [], phase = 'idle' }) {
  const containerRef = useRef();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [tick, setTick] = useState(0);
  const reducedMotion = useMemo(() => (
    typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ), []);

  const working = active && phase !== 'idle' && phase !== 'complete';
  const busy = working || phase === 'transcribing' || phase === 'detecting' || phase === 'slicing' || phase === 'titling' || phase === 'uploading';

  const particles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      arr.push({
        x: Math.random() * 100,
        y: Math.random() * 100,
        r: 1.2 + Math.random() * 1.0,
        color: PARTICLE_COLORS_WEIGHTED[i % PARTICLE_COLORS_WEIGHTED.length],
        opacity: 0.25 + Math.random() * 0.5,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return arr;
  }, []);

  const [liveParticles, setLiveParticles] = useState(particles);

  useEffect(() => {
    setLiveParticles(particles);
  }, [particles]);

  // Animate particle field while processing
  useEffect(() => {
    if (reducedMotion || !busy) return undefined;
    let raf;
    let last = performance.now();
    const speed = phase === 'transcribing' ? 1.35
      : phase === 'detecting' ? 1.1
      : phase === 'slicing' ? 1.5
      : phase === 'titling' ? 0.9
      : 0.7;

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      setLiveParticles((prev) => prev.map((p, i) => {
        let x = p.x + p.vx * speed * (dt * 60);
        let y = p.y + p.vy * speed * (dt * 60);
        if (x < -2) x = 102;
        if (x > 102) x = -2;
        if (y < -2) y = 102;
        if (y > 102) y = -2;
        // Soft swirl toward center during transcription
        if (phase === 'transcribing' || phase === 'detecting') {
          const cx = 50 - x;
          const cy = 50 - y;
          x += cx * 0.004 * speed;
          y += cy * 0.004 * speed;
        }
        const pulse = 0.55 + 0.45 * Math.sin(now / 400 + p.phase + i * 0.2);
        return { ...p, x, y, blink: pulse };
      }));
      setTick(now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [busy, phase, reducedMotion]);

  const connections = useMemo(() => {
    const lines = [];
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const dists = particles.map((q, j) => ({
        j,
        d: Math.hypot(p.x - q.x, p.y - q.y),
      })).filter(x => x.j !== i).sort((a, b) => a.d - b.d).slice(0, 2);
      dists.forEach(({ j }) => {
        if (j > i) lines.push({ a: i, b: j });
      });
    }
    for (let k = 0; k < 6; k++) {
      lines.push({
        a: Math.floor(Math.random() * particles.length),
        b: Math.floor(Math.random() * particles.length),
        long: true,
      });
    }
    return lines;
  }, [particles]);

  const onMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const dy = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setTilt({ x: -dy * 6, y: dx * 6 });
  };
  const onMouseLeave = () => setTilt({ x: 0, y: 0 });

  const isLit = (id) => activeThemes.includes(id);
  const showScan = busy && (phase === 'uploading' || phase === 'transcribing' || phase === 'titling');
  const showRipples = busy && (phase === 'transcribing' || phase === 'detecting' || phase === 'slicing');
  const scanX = 8 + ((tick / 18) % 84);
  const phaseLabel = PHASE_LABEL[phase] || null;

  // Ghost-light orbs during early phases before themes lock in
  const ghostLit = busy && activeThemes.length === 0;

  return (
    <div
      ref={containerRef}
      className={`relative inner-dark aspect-[5/4] w-full overflow-hidden !border !border-white/35 ${busy ? 'theme-graph-busy' : ''}`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{ perspective: 800 }}
      data-testid="theme-graph"
      data-phase={phase}
    >
      {phaseLabel && (
        <div
          className="absolute top-3 right-3 z-10 font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-white/15 bg-black/40 text-white/70"
          data-testid="theme-graph-phase"
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle"
            style={{
              background: phase === 'complete' ? THEME_COLORS.mint : THEME_COLORS.amber,
              boxShadow: phase === 'complete' ? `0 0 8px ${THEME_COLORS.mint}` : `0 0 8px ${THEME_COLORS.amber}`,
              animation: busy ? 'dot-pulse 1.1s ease-in-out infinite' : 'none',
            }}
          />
          {phaseLabel}
        </div>
      )}

      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: 'transform 300ms ease',
        }}
      >
        <defs>
          <linearGradient id="bond-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#20B8F2" />
            <stop offset="50%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#7EE06D" />
          </linearGradient>
          <linearGradient id="scan-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#20B8F2" stopOpacity="0" />
            <stop offset="50%" stopColor="#20B8F2" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#7EE06D" stopOpacity="0" />
          </linearGradient>
          {[...PRIMARY_ORBS, ...SUB_ORBS].map(o => (
            <radialGradient key={o.id} id={`orb-${o.id}`} cx="35%" cy="35%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
              <stop offset="30%" stopColor={o.color} stopOpacity="1" />
              <stop offset="100%" stopColor={o.color} stopOpacity="0.55" />
            </radialGradient>
          ))}
        </defs>

        {/* Particle connections */}
        {connections.map((c, i) => {
          const a = liveParticles[c.a] || particles[c.a];
          const b = liveParticles[c.b] || particles[c.b];
          if (!a || !b) return null;
          const base = c.long ? 0.04 : 0.09;
          const boost = busy ? base * (1.4 + 0.6 * Math.sin(tick / 500 + i)) : base;
          return (
            <line
              key={i}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke="#ffffff"
              strokeWidth={c.long ? 0.15 : 0.25}
              strokeOpacity={boost}
            />
          );
        })}

        {/* Particles */}
        {liveParticles.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={p.r * 0.4 * (busy ? (0.85 + 0.35 * (p.blink || 1)) : 1)}
            fill={p.color}
            opacity={(p.opacity) * (busy ? (0.55 + 0.45 * (p.blink || 1)) : 1)}
          />
        ))}

        {/* Transcription / detect ripples */}
        {showRipples && !reducedMotion && (
          <>
            <circle cx="50" cy="50" r="8" fill="none" stroke="#20B8F2" strokeWidth="0.25" opacity="0.35">
              <animate attributeName="r" values="6;28" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.45;0" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle cx="50" cy="50" r="8" fill="none" stroke="#A78BFA" strokeWidth="0.2" opacity="0.3">
              <animate attributeName="r" values="6;32" dur="2.4s" begin="0.8s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0" dur="2.4s" begin="0.8s" repeatCount="indefinite" />
            </circle>
          </>
        )}

        {/* Bonds */}
        {BONDS.map((b, i) => {
          const from = findOrb(b.from);
          const to = findOrb(b.to);
          const bothLit = isLit(b.from) && isLit(b.to);
          const ghost = ghostLit && b.pri;
          const opacity = bothLit
            ? (b.pri ? 0.55 : 0.3)
            : ghost
              ? 0.12 + 0.1 * Math.sin(tick / 450 + i)
              : (busy && b.pri ? 0.06 : 0);
          return (
            <g key={i}>
              <line
                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke="url(#bond-grad)"
                strokeWidth={b.pri ? 0.5 : 0.28}
                strokeOpacity={opacity}
                style={{ transition: 'stroke-opacity 500ms ease' }}
              />
              {(bothLit || (busy && ghost && i % 2 === 0)) && !reducedMotion && (
                <circle r="0.7" fill="#ffffff" opacity="0.85">
                  <animateMotion
                    dur={`${1.6 + (i % 3) * 0.4}s`}
                    repeatCount="indefinite"
                    path={`M ${from.x} ${from.y} L ${to.x} ${to.y}`}
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* Sub-theme orbs */}
        {SUB_ORBS.map(o => {
          const lit = isLit(o.id);
          return (
            <g
              key={o.id}
              style={{
                opacity: lit ? 1 : 0,
                transform: `scale(${lit ? 1 : 0})`,
                transformOrigin: `${o.x}% ${o.y}%`,
                transition: 'opacity 350ms ease, transform 350ms ease',
              }}
            >
              <circle cx={o.x} cy={o.y} r={o.r * 0.4} fill={`url(#orb-${o.id})`} filter={`drop-shadow(0 0 3px ${o.color})`} />
            </g>
          );
        })}

        {/* Primary orbs */}
        {PRIMARY_ORBS.map((o, idx) => {
          const lit = isLit(o.id);
          const searching = ghostLit;
          const pulse = searching ? 0.22 + 0.12 * Math.sin(tick / 380 + idx) : (lit ? 1 : 0.15);
          const scale = lit ? 1 : searching ? 0.78 + 0.06 * Math.sin(tick / 420 + idx) : 0.7;
          return (
            <g key={o.id}>
              {(lit || searching) && busy && !reducedMotion && (
                <circle
                  cx={o.x}
                  cy={o.y}
                  r={o.r * 0.55}
                  fill="none"
                  stroke={o.color}
                  strokeWidth="0.25"
                  opacity={lit ? 0.35 : 0.2}
                >
                  <animate attributeName="r" values={`${o.r * 0.45};${o.r * 0.75};${o.r * 0.45}`} dur={`${2 + idx * 0.25}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.4;0.05;0.4" dur={`${2 + idx * 0.25}s`} repeatCount="indefinite" />
                </circle>
              )}
              <g
                style={{
                  opacity: pulse,
                  transform: `scale(${scale})`,
                  transformOrigin: `${o.x}% ${o.y}%`,
                  transition: searching ? 'none' : 'opacity 400ms ease, transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <circle cx={o.x} cy={o.y} r={o.r * 0.4} fill={`url(#orb-${o.id})`} filter={`drop-shadow(0 0 4px ${o.color})`} />
              </g>
              <text
                x={o.x}
                y={o.y + o.r * 0.4 + 4}
                textAnchor="middle"
                fill="rgba(255,255,255,0.7)"
                fontSize={2.6}
                fontFamily="JetBrains Mono, monospace"
                letterSpacing="0.18em"
                style={{ opacity: lit ? 1 : searching ? 0.45 : 0.35, transition: 'opacity 400ms ease' }}
              >
                {o.label}
              </text>
            </g>
          );
        })}

        {/* Scan sweep during upload / transcription */}
        {showScan && !reducedMotion && (
          <line
            x1={scanX}
            y1="4"
            x2={scanX}
            y2="96"
            stroke="url(#scan-grad)"
            strokeWidth="0.45"
            opacity="0.9"
            style={{ filter: 'drop-shadow(0 0 4px #20B8F2)' }}
          />
        )}

        {/* Orbital dots when themes are fully lit or slicing */}
        {(activeThemes.length >= 5 || phase === 'slicing' || phase === 'complete') && (
          <>
            <OrbitalDot cx={26} cy={32} rx={12} ry={7} color={THEME_COLORS.cyan} dur={5} />
            <OrbitalDot cx={78} cy={68} rx={10} ry={6} color={THEME_COLORS.mint} dur={7} />
            <OrbitalDot cx={52} cy={50} rx={9} ry={5} color={THEME_COLORS.coral} dur={4} />
          </>
        )}
      </svg>
    </div>
  );
}

function OrbitalDot({ cx, cy, rx, ry, color, dur }) {
  return (
    <g>
      <circle r={0.9} fill={color}>
        <animateMotion
          dur={`${dur}s`}
          repeatCount="indefinite"
          path={`M ${cx + rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy}`}
        />
      </circle>
    </g>
  );
}
